import { useState, useEffect, useCallback } from 'react';
import {
  getLookupCollectionFull,
  addLookupValue,
  updateLookupValue,
  bulkUpdateLookupValues,
  deleteLookupValue,
  reorderLookupValues,
  updateLookupCollectionProperties,
  type LookupCollectionDocument,
} from '../services/firebase';
import type { LookupValue } from '../types';

export interface CollectionProperties {
  isActive: boolean;
  supportsSynonyms: boolean;
  supportsMeasurement: boolean;
  isMultiSelect: boolean;
  supportsGrouping: boolean;
  name?: string;
  description?: string;
}

export function useLookupCollection(collectionName: string) {
  const [collection, setCollection] = useState<LookupCollectionDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!collectionName) return;

    setLoading(true);
    setError(null);
    try {
      const data = await getLookupCollectionFull(collectionName);
      setCollection(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collection');
    } finally {
      setLoading(false);
    }
  }, [collectionName]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addValue = useCallback(
    async (value: Omit<LookupValue, 'id'>) => {
      try {
        await addLookupValue(collectionName, value);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add value');
        throw err;
      }
    },
    [collectionName, refresh]
  );

  const updateValue = useCallback(
    async (valueId: string, updates: Partial<LookupValue>) => {
      try {
        await updateLookupValue(collectionName, valueId, updates);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update value');
        throw err;
      }
    },
    [collectionName, refresh]
  );

  const bulkUpdateValues = useCallback(
    async (updates: Array<{ id: string; patch: Partial<LookupValue> }>) => {
      try {
        await bulkUpdateLookupValues(collectionName, updates);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update values');
        throw err;
      }
    },
    [collectionName, refresh]
  );

  const deleteValue = useCallback(
    async (valueId: string) => {
      try {
        await deleteLookupValue(collectionName, valueId);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete value');
        throw err;
      }
    },
    [collectionName, refresh]
  );

  const reorder = useCallback(
    async (orderedIds: string[]) => {
      try {
        await reorderLookupValues(collectionName, orderedIds);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to reorder values');
        throw err;
      }
    },
    [collectionName, refresh]
  );

  const updateProperties = useCallback(
    async (properties: Partial<CollectionProperties>) => {
      try {
        await updateLookupCollectionProperties(collectionName, properties);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update collection properties');
        throw err;
      }
    },
    [collectionName, refresh]
  );

  return {
    collection,
    values: collection?.values || [],
    properties: collection ? {
      isActive: collection.isActive,
      supportsSynonyms: collection.supportsSynonyms,
      supportsMeasurement: collection.supportsMeasurement,
      isMultiSelect: collection.isMultiSelect,
      supportsGrouping: collection.supportsGrouping,
      name: collection.name,
      description: collection.description,
    } : null,
    loading,
    error,
    refresh,
    addValue,
    updateValue,
    bulkUpdateValues,
    deleteValue,
    reorder,
    updateProperties,
  };
}
